import { pathnameWithoutLocale, teacherDashboardPath } from "@outschool/routes";

// Shared geometry for the server and client skeletons.

export const TEACHER_DASHBOARD_SKELETON_GEOMETRY = {
  header: {
    height: 64,
    maxWidth: 1440,
    horizontalMargin: 24,
    gap: 32,
  },
  logo: {
    desktopWidth: 206,
    desktopHeight: 26,
    mobileWidth: 42,
    mobileHeight: 42,
  },
  page: {
    maxWidth: 1120,
    horizontalPadding: 16,
    verticalPadding: 32,
  },
  dashboard: {
    sidebarWidth: 300,
    gap: 32,
  },
  breakpoints: {
    small: 575,
    medium: 768,
    large: 1000,
  },
} as const;

// MUI tab defaults.
export const EDUCATOR_TOOLBAR_SKELETON = {
  tabLabelWidths: [40, 92, 52, 64, 100, 48],
  tabMinWidth: 90,
  tabHorizontalPadding: 16,
} as const;

// Backpack type scale for the server skeleton.
export const SKELETON_TYPE_SCALE = {
  h3: { fontSize: 24, mdFontSize: 32, lineHeight: 1.2 },
  h4: { fontSize: 22, mdFontSize: 28, lineHeight: 1.25 },
  h5: { fontSize: 22, lineHeight: 1.25 },
  body1: { fontSize: 16, lineHeight: 1.5 },
  body2: { fontSize: 14, lineHeight: 1.5 },
} as const;

export const SKELETON_COLORS = {
  profileGradient: "linear-gradient(118deg, #f0f2ff 31.38%, #FFE8F5 162.62%)",
  outreachBackground: "#F0F3FF",
  resourceBackground: "#fcfdfd",
  resourceBorder: "#b4befe",
} as const;

// Spacing from the dashboard cards.
export const TEACHER_DASHBOARD_SKELETON_LAYOUT = {
  buttonHeight: 52,
  buttonBorderRadius: 999,
  cardPadding: 24,
  cardBorderRadius: 16,
  outreachPaddingY: 16,
  outreachPaddingX: 20,
  resourceBottomPadding: 224,
  profileAvatarSize: 116,
  profileGap: 24,
  blockPaddingY: 32,
  blockGap: 8,
} as const;

// The left column is neutral because educator dashboard variants differ.
const CONTENT_BLOCK_SHAPES = [
  { headingWidth: "46%", lines: 5 },
  { headingWidth: "38%", lines: 6 },
  { headingWidth: "42%", lines: 4 },
];

export const TEACHER_DASHBOARD_SKELETON_CONTENT_BLOCKS: readonly {
  id: string;
  headingWidth: string;
  lines: number;
}[] = Array.from({ length: 8 }, (_unused, index) => ({
  id: `block-${index}`,
  ...CONTENT_BLOCK_SHAPES[index % CONTENT_BLOCK_SHAPES.length]!,
}));

// Mirrors the Tips and Resources sections.
export const TEACHER_DASHBOARD_SKELETON_SIDEBAR_SECTIONS = [
  { id: "tips", itemCount: 4, itemLines: 2 },
  { id: "resources", itemCount: 4, itemLines: 1 },
] as const;

export const SKELETON_BACKGROUND_COLOR = "rgba(0, 0, 0, 0.11)";
export const SKELETON_PULSE_ANIMATION =
  "teacher-dashboard-skeleton-pulse 1.5s ease-in-out 0.5s infinite";
export const SKELETON_PULSE_KEYFRAMES = `
  @keyframes teacher-dashboard-skeleton-pulse {
    0% { opacity: 1; }
    50% { opacity: 0.4; }
    100% { opacity: 1; }
  }
`;

export function isTeacherDashboardPath(pathname: string): boolean {
  const pathnameWithoutTrailingSlash =
    pathnameWithoutLocale(pathname).replace(/\/+$/, "") || "/";
  return pathnameWithoutTrailingSlash === teacherDashboardPath();
}
